import type { FilterSpecification } from 'maplibre-gl';
import { ControlPosition } from './types';

export const MAP_STYLES = {
  streets: 'streets',
  satellite: 'satellite',
} as const;

export const DEFAULT_MAP_CONFIG = {
  style: MAP_STYLES.streets,
  projection: 'mercator',
  center: [-98.58, 39.83] as [number, number], // Center of continental US
  zoom: 3,
  pitch: 0,
  bearing: 0,
  hash: true,
  dragRotate: false,
  pitchWithRotate: false,
  // v6 overscales tiles by default; keep the v5 behavior.
  zoomLevelsToOverscale: undefined,
} as const;

export const DEFAULT_UI_CONFIG = {
  showControls: true,
  showOverlay: true,
  showLegend: true,
  showDummyData: true,
  controlsPosition: 'bottom-left' as ControlPosition,
} as const;

export const CONTROL_CONFIG = {
  navigation: {
    showCompass: true,
    showZoom: true,
    visualizePitch: true,
  },
  fullscreen: true,
  scale: false,
  geolocate: false,
};

// GeoJSON Geometry Types (RFC 7946)
export const GEOMETRY_TYPES = {
  POINT: 'Point',
  MULTI_POINT: 'MultiPoint',
  LINE_STRING: 'LineString',
  MULTI_LINE_STRING: 'MultiLineString',
  POLYGON: 'Polygon',
  MULTI_POLYGON: 'MultiPolygon',
} as const;

export enum DATA_SOURCE_TYPES {
  GEOJSON = 'geojson',
  TILESET = 'tileset',
  READONLY_TILESET = 'readonly-tileset',
  RASTER = 'raster',
}

/**
 * Gray color for unmatched/other categories in styling
 */
export const OTHER_CATEGORY_COLOR = '#808080';

export const PARCELS_LAYER_ID = 'f0e9d8c7-b6a5-4321-fedc-ba9876543210';

export const MAPLIBRE_LAYER_TYPES = [
  'circle',
  'fill',
  'line',
  'symbol',
  'symbol-icon',
  'raster',
] as const;
export type MapLibreLayerType = (typeof MAPLIBRE_LAYER_TYPES)[number];

export const GEOMETRY_FILTERS = {
  CIRCLE: [
    'any',
    ['==', ['geometry-type'], 'Point'],
    ['==', ['geometry-type'], 'MultiPoint'],
  ] as FilterSpecification,
  FILL: [
    'any',
    ['==', ['geometry-type'], 'Polygon'],
    ['==', ['geometry-type'], 'MultiPolygon'],
  ] as FilterSpecification,
  // Includes polygons on purpose: a `line` layer is what strokes their
  // outlines. Use LINE_ONLY when a layer must match line geometry alone.
  LINE: [
    'any',
    ['==', ['geometry-type'], 'LineString'],
    ['==', ['geometry-type'], 'MultiLineString'],
    ['==', ['geometry-type'], 'Polygon'],
    ['==', ['geometry-type'], 'MultiPolygon'],
  ] as FilterSpecification,
  LINE_ONLY: [
    'any',
    ['==', ['geometry-type'], 'LineString'],
    ['==', ['geometry-type'], 'MultiLineString'],
  ] as FilterSpecification,
} as const;
